import { LucideIcon, TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: LucideIcon;
    trend?: {
        value: number;
        direction: 'up' | 'down';
    };
    color?: 'ember' | 'safety' | 'alert' | 'steel';
    delay?: number;
}

const colorClasses = {
    ember: {
        bg: 'from-ember-500 to-ember-600',
        shadow: 'shadow-ember',
        ring: 'ring-ember-500/20',
    },
    safety: {
        bg: 'from-safety-500 to-safety-600',
        shadow: 'shadow-safety',
        ring: 'ring-safety-500/20',
    },
    alert: {
        bg: 'from-alert-500 to-alert-600',
        shadow: 'shadow-alert',
        ring: 'ring-alert-500/20',
    },
    steel: {
        bg: 'from-steel-500 to-steel-600',
        shadow: 'shadow-md',
        ring: 'ring-steel-500/20',
    },
};

export default function MetricCard({
    title,
    value,
    subtitle,
    icon: Icon,
    trend,
    color = 'ember',
    delay = 0,
}: MetricCardProps) {
    const colors = colorClasses[color];

    return (
        <div
            className="card-glass p-6 animate-slideUp hover:shadow-xl transition-all duration-300 group cursor-pointer"
            style={{ animationDelay: `${delay}ms` }}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <p className="text-sm font-medium text-steel-600 mb-1">{title}</p>
                    <h3 className="text-3xl font-display font-bold text-charcoal-950 group-hover:scale-105 transition-transform">
                        {value}
                    </h3>
                    {subtitle && (
                        <p className="text-sm text-steel-500 mt-1">{subtitle}</p>
                    )}
                </div>
                <div className={`w-12 h-12 bg-gradient-to-br ${colors.bg} rounded-xl ${colors.shadow} flex items-center justify-center group-hover:scale-110 transition-transform animate-float`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>

            {trend && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-steel-200">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium ${trend.direction === 'up'
                            ? 'bg-safety-100 text-safety-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                        {trend.direction === 'up' ? (
                            <TrendingUp className="w-3 h-3" />
                        ) : (
                            <TrendingDown className="w-3 h-3" />
                        )}
                        <span>{Math.abs(trend.value)}%</span>
                    </div>
                    <span className="text-xs text-steel-500">vs last month</span>
                </div>
            )}
        </div>
    );
}
